import { useState, useRef, useEffect } from "react"
import { Upload, FileUp, X, Image as ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"

interface FileUploadScreenProps {
  onComplete: (data: { file: File }) => void
  onCancel: () => void
}

export function FileUploadScreen({ onComplete, onCancel }: FileUploadScreenProps) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Clean up object URL on unmount or file change
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  // Listen for clipboard paste
  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items
      if (!items) return

      for (const item of items) {
        if (item.kind === "file") {
          const pasted = item.getAsFile()
          if (pasted) {
            e.preventDefault()
            selectFile(pasted)
            return
          }
        }
      }
    }

    document.addEventListener("paste", handlePaste)
    return () => document.removeEventListener("paste", handlePaste)
  }, [])

  function selectFile(f: File) {
    if (preview) URL.revokeObjectURL(preview)
    setFile(f)
    if (f.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(f))
    } else {
      setPreview(null)
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (selected) selectFile(selected)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) selectFile(dropped)
  }

  function handleClear() {
    setFile(null)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-8" data-testid="file-upload-screen">
      {!file && (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`flex w-full max-w-sm cursor-pointer flex-col items-center gap-4 rounded-xl border-2 border-dashed p-12 transition-colors ${
              dragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            data-testid="drop-zone"
          >
            <Upload className="h-12 w-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center">
              Drop a file here, click to browse, or paste from clipboard
            </p>
            <input
              ref={inputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
          </div>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
        </>
      )}

      {file && (
        <div className="w-full max-w-sm space-y-4">
          {/* Image preview */}
          {preview && (
            <div className="relative overflow-hidden rounded-lg border bg-muted">
              <img
                src={preview}
                alt={file.name}
                className="mx-auto max-h-64 object-contain"
              />
            </div>
          )}

          {/* File info */}
          <div className="flex items-center gap-3 rounded-lg border p-4">
            {file.type.startsWith("image/") ? (
              <ImageIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
            ) : (
              <FileUp className="h-5 w-5 shrink-0 text-muted-foreground" />
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
            </div>
            <button
              onClick={handleClear}
              className="rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => onComplete({ file })} className="flex-1">
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
